import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type {
  PartnerLoginRequest,
  PartnerMe,
  PartnerProgramMembership,
  PartnerSignupRequest,
} from "../../shared/types";
import { partnerApi } from "../lib/api";

interface PartnerAuthValue {
  /** The signed-in partner + their programs, or null when signed out. */
  session: PartnerMe | null;
  /** True while the first /partner/me check is in flight. */
  loading: boolean;
  /** The program the inner app is currently showing (portal switcher). */
  selectedProgramId: string | null;
  /** The membership for the selected program, or null. */
  currentMembership: PartnerProgramMembership | null;
  signup: (body: PartnerSignupRequest) => Promise<PartnerMe>;
  login: (body: PartnerLoginRequest) => Promise<PartnerMe>;
  logout: () => Promise<void>;
  /** Re-checks the session against the API. */
  refresh: () => Promise<void>;
  /** Point the inner app + shell at a program. */
  selectProgram: (programId: string | null) => void;
  /** Replace one membership in place after an onboarding step (no full refetch). */
  applyMembership: (membership: PartnerProgramMembership) => void;
  /** Replace the whole session after a profile / avatar / password change (node P6). */
  applyMe: (me: PartnerMe) => void;
}

const PartnerAuthContext = createContext<PartnerAuthValue | null>(null);

const SELECTED_KEY = "flashadz-partner-program";

function readSelected(): string | null {
  try {
    return localStorage.getItem(SELECTED_KEY);
  } catch {
    return null;
  }
}

/**
 * Real partner session state. It asks the API who is signed in (GET
 * /api/partner/me) on mount and after every auth action, and holds the answer.
 * Nothing here fabricates a partner: signed out is signed out.
 */
export function PartnerAuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<PartnerMe | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedProgramId, setSelectedProgramId] = useState<string | null>(readSelected);

  useEffect(() => {
    let alive = true;
    partnerApi
      .me()
      .then((next) => {
        if (alive) setSession(next);
      })
      .catch(() => {
        if (alive) setSession(null);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  const selectProgram = useCallback((programId: string | null) => {
    setSelectedProgramId(programId);
    try {
      if (programId) localStorage.setItem(SELECTED_KEY, programId);
      else localStorage.removeItem(SELECTED_KEY);
    } catch {
      // Storage can be unavailable (private mode); in-memory selection still applies.
    }
  }, []);

  const refresh = useCallback(async () => {
    const next = await partnerApi.me();
    setSession(next);
  }, []);

  const signup = useCallback(async (body: PartnerSignupRequest) => {
    const next = await partnerApi.signup(body);
    setSession(next);
    return next;
  }, []);

  const login = useCallback(async (body: PartnerLoginRequest) => {
    const next = await partnerApi.login(body);
    setSession(next);
    return next;
  }, []);

  const logout = useCallback(async () => {
    await partnerApi.logout();
    setSession(null);
    setSelectedProgramId(null);
    try {
      localStorage.removeItem(SELECTED_KEY);
    } catch {
      // ignore storage failures on sign out
    }
  }, []);

  const applyMe = useCallback((me: PartnerMe) => {
    setSession(me);
  }, []);

  const applyMembership = useCallback((membership: PartnerProgramMembership) => {
    setSession((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        programs: prev.programs.map((p) =>
          p.programId === membership.programId ? membership : p,
        ),
      };
    });
  }, []);

  const currentMembership = useMemo(
    () => session?.programs.find((p) => p.programId === selectedProgramId) ?? null,
    [session, selectedProgramId],
  );

  const value = useMemo<PartnerAuthValue>(
    () => ({
      session,
      loading,
      selectedProgramId,
      currentMembership,
      signup,
      login,
      logout,
      refresh,
      selectProgram,
      applyMembership,
      applyMe,
    }),
    [
      session,
      loading,
      selectedProgramId,
      currentMembership,
      signup,
      login,
      logout,
      refresh,
      selectProgram,
      applyMembership,
      applyMe,
    ],
  );

  return <PartnerAuthContext.Provider value={value}>{children}</PartnerAuthContext.Provider>;
}

export function usePartnerAuth(): PartnerAuthValue {
  const ctx = useContext(PartnerAuthContext);
  if (!ctx) throw new Error("usePartnerAuth must be used within PartnerAuthProvider");
  return ctx;
}
